import { useEffect, useState } from "react";

import { ArrowLeftIcon, ShoppingCartIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { SalesQuoteStatusPill } from "../../components/salesQuotes/SalesQuotePills";
import useNotification from "../../components/notifications/useNotifications";
import Card from "../../components/shared/Card/Card";
import AgeCell from "../../components/shared/Cells/AgeCell";
import ClickToCopy from "../../components/shared/ClickToCopy";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import Avatar from "../../components/user/Avatar";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useSalesQuotes from "../../data/useSalesQuotes";
import { DiscountType } from "../../models/Basket";
import { PartStatus } from "../../models/Part";
import { SALES_QUOTE_SKIP_REASON_LABELS, SalesQuoteStatus } from "../../models/SalesQuote";
import formatDateTime from "../../utils/formatDateTime";
import openInNewTab from "../../utils/openInNewTab";
import PriceFormat from "../../utils/priceFormat";

// Matches the payment method list in BasketInvoiceInputs
const PAYMENT_METHOD_LABELS: Record<number, string> = {
  0: "Cash",
  1: "Card",
  2: "eBay",
  3: "Stripe",
  4: "Bank Transfer",
  5: "Cheque",
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{children}</dd>
    </div>
  );
}

export default function ViewSalesQuote() {
  const { t } = useTranslation("accounts");
  const { salesQuoteId } = useParams();
  const navigate = useNavigate();
  const { setInfo } = usePageNameContext();
  const { addNotification } = useNotification();
  const { salesQuote, load, cancel } = useSalesQuotes(salesQuoteId, "ViewSalesQuote");

  const [loadOpen, setLoadOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const quote = salesQuote.data?.data;

  useEffect(() => {
    setInfo({
      name: quote ? t("Sales Quote: {{name}}", { name: quote.contactName || quote.accountName || t("No contact") }) : t("Sales Quote"),
      desc: quote ? t("Saved {{date}}", { date: formatDateTime(quote.created, DateTime.DATETIME_MED) }) : "",
    });
  }, [quote?.id, quote?.contactName, t]);

  if (salesQuote.isLoading) return <LoadingWheel />;

  if (!quote) {
    return (
      <Card containerClassName="mt-4">
        <p className="p-4 text-sm text-gray-600">{t("This quote could not be found.")}</p>
      </Card>
    );
  }

  const isOpen = quote.status === SalesQuoteStatus.Open;
  const address = [quote.line1, quote.line2, quote.line3, quote.line4, quote.town, quote.county, quote.postcode]
    .filter(Boolean)
    .join(", ");

  const handleLoad = () => {
    load.mutate(quote.id, {
      onSuccess: (response) => {
        const result = response.data;
        addNotification({
          variant: "success",
          primaryText: result.loadedCount === 1
            ? t("Loaded {{count}} item into your basket", { count: result.loadedCount })
            : t("Loaded {{count}} items into your basket", { count: result.loadedCount }),
          secondaryText: t("Open the basket from the top bar to check out."),
        });
        if (result.skipped.length > 0) {
          addNotification({
            variant: "warn",
            primaryText: result.skipped.length === 1
              ? t("{{count}} item could not be loaded", { count: result.skipped.length })
              : t("{{count}} items could not be loaded", { count: result.skipped.length }),
            secondaryText: result.skipped
              .map((s) => `${s.tagNumber ?? s.title ?? t("Part")}: ${t(SALES_QUOTE_SKIP_REASON_LABELS[s.reason] ?? "unavailable")}`)
              .join("; "),
          });
        }
        setLoadOpen(false);
      },
    });
  };

  return (
    <div className="mt-4 space-y-4">
      <ConfirmModal
        open={loadOpen}
        setOpen={setLoadOpen}
        title={t("Load this quote into your basket?")}
        message={t("The quoted items and prices are added to your current basket. Anything already sold is skipped. Checking out will mark the quote as converted.")}
        confirmButtonText={t("Load into basket")}
        onConfirm={handleLoad}
        isLoading={load.isLoading}
        Icon={<ShoppingCartIcon className="h-6 w-6 text-primary-600" aria-hidden="true" />}
        iconBg="bg-primary-100"
      />
      <ConfirmModal
        open={cancelOpen}
        setOpen={setCancelOpen}
        title={t("Cancel this quote?")}
        message={t("The quote stays on record as cancelled. Nothing changes on the parts.")}
        confirmButtonText={t("Cancel quote")}
        onConfirm={() => cancel.mutate(quote.id, { onSuccess: () => setCancelOpen(false) })}
        isLoading={cancel.isLoading}
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <ButtonInput label="" classes={undefined} onClick={() => navigate("/sales-quotes")}>
          <span className="flex items-center gap-1"><ArrowLeftIcon className="h-4 w-4" /> {t("Back to quotes")}</span>
        </ButtonInput>
        <div className="flex flex-wrap items-center gap-2">
          <SalesQuoteStatusPill status={quote.status} />
          {isOpen && (
            <>
              <ButtonInput label={t("Cancel Quote")} classes="!bg-red-600 hover:!bg-red-700" onClick={() => setCancelOpen(true)} />
              <ButtonInput label={t("Load into Basket")} classes={undefined} onClick={() => setLoadOpen(true)} />
            </>
          )}
        </div>
      </div>

      {quote.status === SalesQuoteStatus.Converted && quote.convertedInvoiceId && (
        <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {t("Converted {{date}}", { date: formatDateTime(quote.convertedOn, DateTime.DATETIME_MED) })} —{" "}
          <Link className="font-medium underline" to={`/invoices/${quote.convertedInvoiceId}`}>{t("view the invoice")}</Link>.
        </div>
      )}
      {quote.status === SalesQuoteStatus.Cancelled && (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 flex items-center gap-2">
          {t("Cancelled {{date}}", { date: formatDateTime(quote.cancelledOn, DateTime.DATETIME_MED) })}
          {quote.cancelledById && <>{t("by")} <Avatar userId={quote.cancelledById} classes="h-6 w-6" /></>}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card title={t("Customer")}>
          <dl className="grid grid-cols-1 gap-4 p-4">
            <Field label={t("Contact")}>{quote.contactName || <span className="text-gray-400">{t("Not given")}</span>}</Field>
            <Field label={t("Phone")}>{quote.contactPhone ? <ClickToCopy text={quote.contactPhone} /> : <span className="text-gray-400">{t("Not given")}</span>}</Field>
            <Field label={t("Email")}>{quote.contactEmail ? <ClickToCopy text={quote.contactEmail} /> : <span className="text-gray-400">{t("Not given")}</span>}</Field>
            <Field label={t("Account")}>
              {quote.accountId ? (
                <Link className="text-primary-600 hover:underline" to={`/accounts/${quote.accountId}`}>{quote.accountName ?? t("View account")}</Link>
              ) : (
                <span className="text-gray-400">{t("No account")}</span>
              )}
            </Field>
            <Field label={t("Address")}>{address || <span className="text-gray-400">{t("Not given")}</span>}</Field>
          </dl>
        </Card>

        <Card title={t("Quote")}>
          <dl className="grid grid-cols-2 gap-4 p-4">
            <Field label={t("Quoted total")}><span className="text-lg font-semibold">{PriceFormat(quote.quotedTotal)}</span></Field>
            <Field label={t("Age")}>
              <div className="flex flex-col gap-0.5">
                <AgeCell created={quote.created} />
                <span className="text-xs text-gray-500">{formatDateTime(quote.created, DateTime.DATETIME_MED)}</span>
              </div>
            </Field>
            <Field label={t("Shipping")}>{quote.shippingPolicyName ?? <span className="text-gray-400">{t("Not set")}</span>}</Field>
            <Field label={t("Payment method")}>{quote.paymentMethod != null ? t(PAYMENT_METHOD_LABELS[quote.paymentMethod] ?? "Unknown") : <span className="text-gray-400">{t("Not set")}</span>}</Field>
            <Field label={t("Tax")}>{quote.addTaxToTotal ? t("Added on top") : t("Included in prices")}</Field>
            <Field label={t("Discount")}>
              {quote.discountValue > 0
                ? quote.discountType === DiscountType.Percentage ? `${quote.discountValue}%` : PriceFormat(quote.discountValue)
                : <span className="text-gray-400">{t("None")}</span>}
            </Field>
            <Field label={t("Created by")}><Avatar userId={quote.createdById} classes="h-8 w-8" /></Field>
          </dl>
        </Card>

        <Card title={t("Notes")}>
          {quote.notes ? (
            <div className="prose prose-sm max-w-none p-4 text-gray-900" dangerouslySetInnerHTML={{ __html: quote.notes }} />
          ) : (
            <p className="p-4 text-sm text-gray-400">{t("No notes")}</p>
          )}
        </Card>
      </div>

      <Card title={t("Items ({{count}})", { count: quote.items.length })}>
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{t("Tag")}</th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{t("Part")}</th>
              <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">{t("Quoted price")}</th>
              <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">{t("Tax rate")}</th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{t("Now")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {quote.items.map((item) => {
              const gone = item.partStatus === PartStatus.Sold || item.partStatus === PartStatus.Deleted || item.partStatus == null;
              return (
                <tr
                  key={item.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => openInNewTab(`/stock/${item.partId}`)}
                >
                  <td className="px-4 py-2 font-mono text-xs text-gray-700">{item.tagNumber ?? "—"}</td>
                  <td className="px-4 py-2 text-gray-900">{item.title ?? <span className="text-gray-400 italic">{t("Untitled part")}</span>}</td>
                  <td className="px-4 py-2 text-right font-semibold text-gray-900">{item.basketPrice != null ? PriceFormat(item.basketPrice) : "—"}</td>
                  <td className="px-4 py-2 text-right text-gray-700">{item.taxRate != null ? `${item.taxRate}%` : "—"}</td>
                  <td className="px-4 py-2">
                    {item.partStatus == null ? (
                      <span className="text-xs text-gray-400">{t("Part removed")}</span>
                    ) : gone ? (
                      <span className="text-xs font-semibold text-red-600">{t(PartStatus[item.partStatus])}</span>
                    ) : (
                      <span className="text-xs text-green-700">{t("Available")}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
